import clsx from 'clsx'
import { useState } from 'react'

import { BottomSheet } from 'components/BottomSheet'
import { RightDrawer } from 'components/RightDrawer'
import { Button, useBreakpoint } from 'slab'
import { type GrapeMoney } from 'types'

import { SmartBudgetPlanCard } from './PlanCard/PlanCard'
import { type MedicalPlanDisplayItem } from '../../medicalPlanTypes'
import {
  type CoverageType,
  type DentalPlan,
  type SmartBudgetPlanData,
  type VisionPlan,
} from '../../types'

type BenefitPlan = MedicalPlanDisplayItem | DentalPlan | VisionPlan

interface ViewingSimilarPlansProps<T extends BenefitPlan> {
  setOpenedPlan: (plan: T | null) => void
  onSelectPlan: (plan: T) => void
  dismissPopover: () => void
}

interface PlanDetailsWithButtonsProps<T extends BenefitPlan> extends ViewingSimilarPlansProps<T> {
  children: React.ReactNode
  openedPlan: T
}

function PlanDetailsWithButtons<T extends BenefitPlan> ({
  children,
  openedPlan,
  setOpenedPlan,
  onSelectPlan,
  dismissPopover,
}: PlanDetailsWithButtonsProps<T>) {
  return (
    <div className='flex flex-col h-full w-full'>
      <div className='flex-1 overflow-y-auto pr-10 -mr-10'>
        {children}
      </div>
      <div className='gap-3 bg-white w-full flex justify-end pt-6 border-t border-gray-200'>
        <Button
          variant='secondary'
          size='large'
          onClick={() => setOpenedPlan(null)}
        >
          Back
        </Button>
        <Button
          variant='primary'
          size='large'
          onClick={() => {
            onSelectPlan(openedPlan)
            dismissPopover()
          }}
        >
          Select plan
        </Button>
      </div>
    </div>
  )
}

interface PlansListProps<T extends BenefitPlan> extends ViewingSimilarPlansProps<T> {
  popoverTitle: string
  allPlans: T[]
  getPlanKey: (plan: T) => string
  selectedPlanKey: string
  planToPlanData: (plan: T) => SmartBudgetPlanData
  selectedPlanPremium: GrapeMoney
  remainingAllowance: number
}

function PlansList<T extends BenefitPlan> ({
  popoverTitle,
  allPlans,
  getPlanKey,
  selectedPlanKey,
  planToPlanData,
  selectedPlanPremium,
  remainingAllowance,
  onSelectPlan,
  dismissPopover,
  setOpenedPlan,
}: PlansListProps<T>) {
  return (
    <>
      <div className='text-main body-bold'>{popoverTitle}</div>
      {allPlans
        .filter((plan) => getPlanKey(plan) !== selectedPlanKey)
        .map((plan) => (
          <SmartBudgetPlanCard
            key={getPlanKey(plan)}
            planData={planToPlanData(plan)}
            selectedPlanPremium={selectedPlanPremium}
            remainingAllowance={remainingAllowance}
            onSelectPlan={() => {
              onSelectPlan(plan)
              dismissPopover()
            }}
            onViewPlanDetails={() => setOpenedPlan(plan)}
          />
        ))}
    </>
  )
}

interface SmartBudgetPlanProps<T extends BenefitPlan> {
  allPlans: T[]
  selectedPlan: T
  getPlanKey: (plan: T) => string
  planToPlanData: (plan: T) => SmartBudgetPlanData
  planToPlanPremium: (plan: T) => GrapeMoney
  remainingAllowance: number
  coverageType: CoverageType
  viewingCoverageType: CoverageType | null
  setViewingCoverageType: (planType: CoverageType | null) => void
  onSelectPlan: (plan: T) => void
  renderPlanDetails: (plan: T, handleClosePanel: () => void) => React.ReactNode
  popoverTitle: string
  planCannotBeChanged?: boolean
}

export function SmartBudgetPlan<T extends BenefitPlan> ({
  allPlans,
  selectedPlan,
  getPlanKey,
  planToPlanData,
  planToPlanPremium,
  remainingAllowance,
  coverageType,
  viewingCoverageType,
  setViewingCoverageType,
  onSelectPlan,
  renderPlanDetails,
  popoverTitle,
  planCannotBeChanged = false,
}: SmartBudgetPlanProps<T>) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const [openedPlan, setOpenedPlan] = useState<T | null>(null)

  const selectedPlanKey = getPlanKey(selectedPlan)
  const selectedPlanPremium = planToPlanPremium(selectedPlan)

  const isViewingSimilarPlans = viewingCoverageType === coverageType
  const isViewingPlanDetails = openedPlan !== null
  const isPopoverOpen = isViewingSimilarPlans || isViewingPlanDetails

  const handleClose = () => {
    setViewingCoverageType(null)
    setOpenedPlan(null)
  }

  const trackEvent = (eventName: string, plan: T) => {
    window.segment?.track(eventName, {
      coverageType,
      plan_id: getPlanKey(plan),
      plan_name: planToPlanData(plan).name,
      plan_carrier: planToPlanData(plan).carrier,
      plan_premium: planToPlanPremium(plan).formatted_amount,
    })
  }

  const InnerPopoverContent = ({ dismissPopover }: { dismissPopover: () => void }) => {
    const handleSelectPlan = (plan: T) => {
      onSelectPlan(plan)
      trackEvent('[EE] Smart budget plan selected', plan)
    }

    if (isViewingPlanDetails) {
      if (!isViewingSimilarPlans) {
        return (
          <div className='flex flex-col h-full w-full'>
            <div className='flex-1 overflow-y-auto pr-10 -mr-10'>
              {renderPlanDetails(openedPlan, handleClose)}
            </div>
          </div>
        )
      }
      return (
        <PlanDetailsWithButtons
          openedPlan={openedPlan}
          setOpenedPlan={setOpenedPlan}
          onSelectPlan={handleSelectPlan}
          dismissPopover={dismissPopover}
        >
          {renderPlanDetails(openedPlan, handleClose)}
        </PlanDetailsWithButtons>
      )
    }
    if (isViewingSimilarPlans) {
      return (
        <PlansList
          popoverTitle={popoverTitle}
          allPlans={allPlans}
          getPlanKey={getPlanKey}
          selectedPlanKey={selectedPlanKey}
          planToPlanData={planToPlanData}
          selectedPlanPremium={selectedPlanPremium}
          remainingAllowance={remainingAllowance}
          onSelectPlan={handleSelectPlan}
          dismissPopover={dismissPopover}
          setOpenedPlan={setOpenedPlan}
        />
      )
    }
    return null
  }

  const popoverContent = (dismissPopover: () => void, topPadding: string = '0') => (
    <div className={clsx('flex flex-col gap-4 h-full', `pt-${topPadding}`)}>
      <InnerPopoverContent dismissPopover={dismissPopover} />
    </div>
  )

  return (
    <div className='mt-4'>
      <SmartBudgetPlanCard
        planData={planToPlanData(selectedPlan)}
        isSelectedPlan
        hideSimilarPlansButton={planCannotBeChanged || allPlans.length <= 1}
        viewingSimilarPlans={isViewingSimilarPlans}
        onViewSimilarPlans={() => {
          setViewingCoverageType(coverageType)
          trackEvent('[EE] Smart budget view similar plans clicked', selectedPlan)
        }}
        onViewPlanDetails={() => {
          setOpenedPlan(selectedPlan)
          trackEvent('[EE] Smart budget plan details clicked', selectedPlan)
        }}
      />
      {breakpointIsAtLeast('lg')
        ? (
          <RightDrawer
            open={isPopoverOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleClose()
              }
            }}
            width='xl'
            onClose={handleClose}
          >
            {({ dismissDrawer }) => popoverContent(dismissDrawer)}
          </RightDrawer>
          )
        : (
          <BottomSheet
            open={isPopoverOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleClose()
              }
            }}
            onClose={handleClose}
            showCloseButton
          >
            {({ dismissSheet }) => <div className='p-4'>{popoverContent(dismissSheet, '8')}</div>}
          </BottomSheet>
          )}
    </div>
  )
}
